import { inject, Injectable, InjectionToken } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { AuthEvent, RefreshTrigger, ResourceTokens } from '@ninety/mobile/platform/auth';
import { NetworkService } from '@ninety/mobile/platform/services';

import { AuthFailureError, classifyFailure } from './auth-failure';
import { AuthorizationServer } from './authorization-server.service';
import { CommandRunner } from './command-runner.service';
import { EntitlementsApiService } from './entitlements-api.service';
import { selectSession } from './session.selectors';
import { ExchangedTokens, TokenExchangeService } from './token-exchange.service';

/**
 * How the machine waits.
 *
 * Injectable for the same reason `AUTH_CLOCK` is: asserting on retry backoff should not cost a spec
 * four seconds or a set of fake timers.
 */
export const AUTH_DELAY = new InjectionToken<(ms: number) => Promise<void>>('AuthDelay', {
  providedIn: 'root',
  factory: () => ms => new Promise(resolve => setTimeout(resolve, ms)),
});

/**
 * The Commands that turn a credential into a session.
 *
 * Both of these end at the same place — a complete set of Resource Tokens — and differ in what they
 * start from: a Grant that just finished hands over an ID Token, while a restore starts from
 * whatever the session already holds, and only reaches for the Durable Root when it holds nothing.
 *
 * ## Why Session Restore is one Command
 *
 * Its legs — refresh at the Authorization Server, Token Exchange, fetch the Entitlements Token —
 * always run together and fail together. Splitting them into separate Commands would put
 * intermediate states on the machine that nothing decides anything from, and would let a caller
 * stop early and produce a session with no Scopes.
 *
 * ## Why a failure here is not a Sign-out
 *
 * Every failure is classified, and only one of them ends a session: a Resource Server that answered
 * 401 or 403. Offline, timeouts, 5xx and a partial response all stay retryable, and `transition()`
 * decides what that means — backoff while attempts remain, then back to the session that was already
 * working.
 *
 * A failure `classifyFailure` does not recognise is left to escape — see `auth-failure.ts`.
 */
@Injectable()
export class SessionTokensEffects {
  private readonly commands = inject(CommandRunner);
  private readonly store = inject(Store);
  private readonly authorizationServer = inject(AuthorizationServer);
  private readonly exchange = inject(TokenExchangeService);
  private readonly entitlements = inject(EntitlementsApiService);
  private readonly network = inject(NetworkService);
  private readonly wait = inject(AUTH_DELAY);

  /** The last step of a Grant: the Authorization Server said yes, now the Resource Server must. */
  readonly exchangeIdToken$ = createEffect(() =>
    this.commands.on('exchangeIdToken', async (command): Promise<AuthEvent> => {
      try {
        const exchanged = await firstValueFrom(this.exchange.exchange(command.idToken));
        return { type: 'resourceTokensObtained', tokens: await this.withEntitlements(exchanged) };
      } catch (error) {
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  /**
   * The same last step, for the Grant that ends in a code rather than a token.
   *
   * Its own handler rather than a branch inside `exchangeIdToken$`, because the two Commands carry
   * different credentials and a single handler would have to accept both and pick — which is the
   * shape that lets an Azure code be posted as an ID Token. They converge one line later, on the
   * same `withEntitlements` and the same event.
   */
  readonly exchangeMicrosoftCode$ = createEffect(() =>
    this.commands.on('exchangeMicrosoftCode', async (command): Promise<AuthEvent> => {
      try {
        const exchanged = await firstValueFrom(this.exchange.exchangeMicrosoftCode(command.code));
        return { type: 'resourceTokensObtained', tokens: await this.withEntitlements(exchanged) };
      } catch (error) {
        return { type: 'grantFailed', cause: classifyFailure(error) };
      }
    })
  );

  readonly sessionRestore$ = createEffect(() =>
    this.commands.on('sessionRestore', async (command): Promise<AuthEvent> => {
      try {
        // Before the backoff, not after: a device with no path should reach the offline screen now,
        // and the ladder exists for requests that failed rather than for requests never sent.
        if (!(await this.network.isConnected())) {
          return { type: 'refreshFailed', cause: { kind: 'deviceOffline' } };
        }

        // Backoff, when the machine asked for it. Nothing cancels this: a restore that lands after
        // the session ended is an illegal pair, which the machine already ignores and logs.
        if (command.afterMs) await this.wait(command.afterMs);

        return { type: 'resourceTokensObtained', tokens: await this.restore(command.trigger) };
      } catch (error) {
        return { type: 'refreshFailed', cause: classifyFailure(error) };
      }
    })
  );

  /**
   * Re-mint a session for the company user it is already for — or, for a Company Switch, a
   * different one.
   *
   * ## Why a refresh names a company user at all
   *
   * `POST /Login` takes an ID Token and nothing else, and the Authorization Server has no notion of
   * a company — so it answers with whichever company user the Resource Server picks. Refreshing
   * that way would quietly move someone between companies on an ordinary app resume. `GET /Refresh`
   * is the one call that can name one, so anything holding tokens refreshes through it, naming the
   * company user those tokens are for.
   *
   * That makes a Company Switch the same request with one substitution, which is why it is not a
   * separate path: `companyUserId` on the state overrides the company user the current tokens carry.
   *
   * A cold start is the only restore with no company user to name, and the only one the Durable Root
   * can serve on its own.
   *
   * ## Why this reads the state
   *
   * Neither the Resource Refresh Token nor the company user can travel on the Command — Commands
   * are data on the wire and the first is a secret. Reading them here is safe because `stateAdvanced`
   * is dispatched before any `commandIssued`, so by the time this runs the state is the `refreshing`
   * one that asked for it.
   */
  private async restore(trigger: RefreshTrigger): Promise<ResourceTokens> {
    const state = await firstValueFrom(this.store.select(selectSession));

    // Both of these are unreachable unless the state and the Command disagree, which is a bug
    // rather than a failed restore, and is reported as one: `auth.command_failed`, and the state
    // left alone. What a switch must not do is fall through to the company user below and report a
    // switch that never happened.
    if (state.tag !== 'refreshing') {
      throw new Error(`sessionRestore ran against ${state.tag}`);
    }
    if (trigger === 'companySwitch' && (state.companyUserId === undefined || state.stale === null)) {
      throw new Error('sessionRestore{companySwitch} ran with no company user to switch to');
    }

    if (state.stale === null) {
      const idToken = await this.authorizationServer.refreshIdToken();
      return this.withEntitlements(await firstValueFrom(this.exchange.exchange(idToken)));
    }

    const companyUserId = state.companyUserId ?? state.stale.ninetyUserId;
    const exchanged = this.exchange.forCompanyUser(companyUserId, state.stale.refreshToken);
    return this.withEntitlements(await firstValueFrom(exchanged));
  }

  /**
   * The second leg at the Resource Server, which no caller may skip.
   *
   * The Entitlements Token is fetched with the Access Token that was minted a moment ago, and the
   * pair is returned as one value. That is what makes them impossible to hold independently, and
   * why `EntitlementsApiService` has exactly one caller: this line.
   */
  private async withEntitlements(exchanged: ExchangedTokens): Promise<ResourceTokens> {
    const entitlementsToken = await firstValueFrom(this.entitlements.fetch(exchanged.accessToken));

    if (!entitlementsToken) {
      throw new AuthFailureError(
        { kind: 'malformedResponse', detail: 'emptyEntitlements' },
        'the Entitlements Token was empty'
      );
    }

    return { ...exchanged, entitlementsToken };
  }
}
